import { ShieldCheck, AlertTriangle, Lightbulb, Sparkles } from 'lucide-react';

export const gantsArticle = {
  slug: 'gants-moto-homologues',
  weekVolume: 20,
  weekYear: 2026,
  monthLabel: 'Juin 2026',
  category: 'Équipement',
  shortTitle: 'Gants homologués CE',
  title: 'Tes mains méritent mieux',
  kicker: 'EN 13594 — gants moto homologués',
  subtitle: "En cas de chute, le réflexe naturel est de mettre les mains en avant. Sans gants homologués, c'est la peau et les os qui paient. Voici ce que dit la loi en 2026.",
  heroImage: '/images/articles/enquete-02-gants.png',
  heroImageAlt: 'Gants moto en cuir noir avec coques rigides — équipement CE homologué, obligatoire depuis novembre 2016 pour tous les usagers de deux-roues motorisés',
  coverPoster: '/images/articles/poster-02-gants-horizontal.png',
  coverPosterAlt: 'Poster CODE 2 ROUES — Dossier Enquête #2 : Gants moto, 5 étapes (protection, adhérence, confort, ajustement, entretien)',
  posterHorizontal: '/images/articles/poster-02-gants-horizontal.png',
  posterVertical: '/images/articles/poster-02-gants.png',
  posterVerticalAlt: 'Poster vertical CODE 2 ROUES — Dossier Enquête #2 : Gants, 5 étapes pour bien les choisir et les utiliser (protection, adhérence, confort, ajustement, entretien)',
  topRightKicker: { line1: 'Conseil ECSR', line2: 'EPI obligatoire' },
  readTimeMin: 4,
  seoDescription: "Gants moto homologués CE EN 13594 obligatoires depuis 2016. Sanctions, niveaux KP/sans KP, conseils d'achat. Le guide ECSR 2026.",
  seoKeywords: 'gants moto homologués, EN 13594, KP, R431-1-2, équipement obligatoire moto, gants CE',
  about: 'Équipement de protection individuelle du motard, gants homologués',
  source: { label: 'Sécurité routière', url: 'https://www.securite-routiere.gouv.fr', refs: 'Article R431-1-2 du Code de la route, norme EN 13594:2015.' },
  chapo: {
    kicker: 'En bref',
    body: "Depuis le <strong>20 novembre 2016</strong>, le port de gants homologués CE est <strong>obligatoire</strong> sur tout véhicule à moteur à deux ou trois roues (article <strong>R431-1-2</strong>). La norme de référence : <strong>EN 13594:2015</strong>.",
    sideStat: {
      kicker: 'Sanction',
      big: '68 €',
      body: "Amende forfaitaire <strong>3ᵉ classe</strong> + retrait de <strong>1 point</strong> en cas de non-port. Conducteur ET passager concernés.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "En cas de chute à 50 km/h, glisser sur 10 mètres sans gants = chair arrachée et tendons exposés. Le gant homologué fait la différence entre une éraflure et la greffe.",
    source: 'Études IFSTTAR — Mémoire collective ECSR',
  },
  sections: [
    {
      part: 'Partie 1 — La loi',
      title: "Que dit l'article R431-1-2 ?",
      paragraphs: [
        "Depuis le <strong>20 novembre 2016</strong>, tout conducteur ou passager d'un véhicule motorisé à deux ou trois roues doit porter des gants <strong>homologués CE</strong>. La marque CE doit être <strong>visible et permanente</strong> sur le gant.",
      ],
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Sanction', title: "Pas de gants ou gants non CE", body: "<strong>Amende 68 €</strong> + retrait de <strong>1 point</strong>. La sanction est la même pour le passager." },
        { icon: ShieldCheck, accent: '#16a34a', kicker: 'Norme', title: 'EN 13594:2015', body: "Niveau de protection 1 (loisir) ou 2 (sport). Marquage <strong>CE + pictogramme moto</strong> sur la manchette." },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 2 — Comprendre les marquages',
      title: 'Décrypter les pictogrammes des gants',
      normCards: [
        { code: '1', color: '#1d4ed8', title: 'Niveau 1 — Loisir', body: "Résistance à l'abrasion, à la déchirure et à la coupure standard. <strong>Suffisant pour la circulation urbaine</strong> et trajets courts." },
        { code: '2', color: '#16a34a', title: 'Niveau 2 — Sport', body: "Niveau supérieur. Recommandé pour <strong>autoroute, longs trajets, vitesses élevées</strong>. Coque rigide aux articulations." },
        { code: 'KP', color: '#cf2031', title: 'KP — Knuckle Protection', body: "Marquage spécifique = <strong>protection des phalanges</strong> homologuée. À privilégier — première zone touchée à la chute." },
      ],
      callouts: [
        { icon: Lightbulb, accent: '#1d4ed8', body: "<strong>Astuce :</strong> sur la manchette du gant, cherche le pictogramme moto + <strong>EN 13594</strong> + niveau 1 ou 2. Sans ce marquage, le gant n'est <strong>pas conforme</strong> à la loi française." },
      ],
    },
    {
      part: 'Partie 3 — Bien choisir',
      title: 'Les 5 critères pour des gants efficaces',
      checkLists: [
        {
          kicker: 'À privilégier', accent: '#16a34a',
          items: [
            { ok: true, text: '<strong>Coques rigides</strong> aux phalanges (KP) et au poignet' },
            { ok: true, text: '<strong>Cuir</strong> ou textile renforcé sur la paume (zone de glissement)' },
            { ok: true, text: '<strong>Manchette longue</strong> couvrant le poignet (au-dessus de la veste)' },
            { ok: true, text: 'Système de <strong>fermeture serrée</strong> (velcro double + zip)' },
            { ok: true, text: 'Coutures <strong>renforcées</strong> entre les doigts' },
          ],
        },
        {
          kicker: 'À éviter absolument', accent: '#cf2031',
          items: [
            { ok: false, text: 'Gants sans marquage CE (vélo, jardinage, mitaines de cuir)' },
            { ok: false, text: 'Gants trop grands : ils <strong>tournent</strong> sur la main lors de la chute' },
            { ok: false, text: 'Manchette trop courte : laisse le poignet exposé' },
            { ok: false, text: "Gants d'occasion abîmés ou ayant déjà subi une chute" },
            { ok: false, text: 'Coques rigides cassées ou décollées' },
          ],
        },
      ],
    },
    {
      part: 'Partie 4 — Été comme hiver',
      title: 'Choisir selon la saison',
      cards: [
        { icon: Sparkles, accent: '#f59e0b', kicker: 'Été', title: 'Gants ventilés', body: "<strong>Cuir perforé</strong> ou mesh + protections rigides. Restent <strong>obligatoires</strong> même en pleine chaleur." },
        { icon: Sparkles, accent: '#0891b2', kicker: 'Mi-saison', title: 'Gants polyvalents', body: "Cuir pleine fleur + doublure. <strong>Solution la plus polyvalente</strong> — couvre 80 % des sorties." },
        { icon: Sparkles, accent: '#1d4ed8', kicker: 'Hiver', title: 'Gants chauffés ou doublés', body: "Membrane <strong>imperméable + thermique</strong>. Privilégier les gants chauffants USB pour températures négatives." },
      ],
      cardsCols: 3,
    },
  ],
  quiz: [
    { id: 'q1', question: 'Depuis quelle date le port de gants moto homologués est-il obligatoire en France ?', options: ['1er janvier 2010', '20 novembre 2016', '1er juillet 2020', "Pas encore obligatoire, c'est recommandé"], correctIndex: 1, explain: "Article R431-1-2 du Code de la route, applicable depuis le 20 novembre 2016." },
    { id: 'q2', question: 'Quelle est la norme européenne de référence pour les gants moto ?', options: ['EN 1621-1', 'EN 13594:2015', 'EN 17092', 'ECE 22-06'], correctIndex: 1, explain: "EN 13594:2015 = norme spécifique aux gants moto. EN 1621-1 concerne les protections d'articulations, ECE 22-06 les casques." },
    { id: 'q3', question: "Que signifie le marquage 'KP' sur un gant moto ?", options: ["Knee Protection", 'Knuckle Protection (protection des phalanges)', 'Kevlar Padded', 'Knit Polymer'], correctIndex: 1, explain: "KP = Knuckle Protection. Le gant a une protection homologuée des phalanges, première zone touchée en chute." },
    { id: 'q4', question: 'Quelle est la sanction pour non-port de gants conformes à moto ?', options: ['Avertissement', 'Amende 68 € + 1 point retiré', 'Amende 135 € + 3 points retirés', 'Suspension du permis'], correctIndex: 1, explain: "Amende forfaitaire de 3ᵉ classe = 68 € + retrait de 1 point. Pour le conducteur ET le passager." },
    { id: 'q5', question: 'Lequel de ces critères est ESSENTIEL pour un gant homologué ?', options: ['Couleur fluo', 'Marquage CE + EN 13594 visible', 'Cuir épais uniquement', 'Marque connue'], correctIndex: 1, explain: "Le marquage CE + EN 13594 (niveau 1 ou 2) doit être visible et permanent. C'est le SEUL gage légal d'homologation." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: "L'équipement complet du motard",
    body: "Au-delà des gants : casque, blouson, pantalon, bottes, dorsale. Découvre les normes et les obligations de chaque EPI.",
    primary: { label: "Voir l'équipement complet", to: '/magazine/equipement-complet-motard-2026' },
    secondary: { label: 'Lexique EPI', to: '/lexique?slug=epi-equipement-protection' },
  },
};
